// Section 22 shell and 24.14: UI-only actions (tabs, overlays, hover, filters, the End season
// confirm). None of them touch the game or the draft.
import type { PersonId, PromptId } from '../../sim/api';
import { withBoundary } from '../boundary';
import { requireGame } from '../guards';
import { enterDeciding } from '../session';
import { patchUi, settings, status, ui, type Overlay, type Tab } from '../state';
import type { ListFilter, PeopleGrouping, PeopleSortKey } from '../types';
import { endSeason } from './game';

export const setTab = (tab: Tab): void => {
  patchUi({ tab });
};

export const openOverlay = (overlay: Exclude<Overlay, null>): void => {
  patchUi({ overlay, confirmingEnd: false });
};

/** Open the saves overlay on its save or load section. */
export const openSaves = (mode: 'save' | 'load'): void => {
  patchUi({ overlay: 'saves', savesMode: mode, confirmingEnd: false });
};

/**
 * Close the open overlay. Closing the report dismisses it (phase 1 -> 2). The shortage dialog
 * cannot be closed here: it owns its own Esc handling and ends with an answer (Section 22).
 */
export const closeOverlay = withBoundary((): void => {
  const overlay = ui.value.overlay;
  if (overlay === 'shortage' || status.value === 'paused') return;
  if (overlay === 'report' && status.value === 'reporting') {
    patchUi({ overlay: null });
    enterDeciding(requireGame());
    return;
  }
  patchUi({ overlay: null, promptId: overlay === 'event' ? null : ui.value.promptId });
});

/** Open an event prompt from the report or the header; the report is dismissed first. */
export const openPrompt = withBoundary((promptId: PromptId): void => {
  if (status.value === 'reporting') enterDeciding(requireGame());
  patchUi({ overlay: 'event', promptId });
});

export const hoverPerson = (personId: PersonId | null): void => {
  if (ui.value.hoveredPersonId !== personId) patchUi({ hoveredPersonId: personId });
};

export const openPersonCard = (personId: PersonId): void => {
  patchUi({ selectedPersonId: personId, overlay: 'person', confirmingEnd: false });
};

export const setListFilter = (filter: ListFilter): void => {
  patchUi({ listFilter: filter });
};

/** Sort the People table; choosing the same column again flips the direction. */
export const setPeopleSort = (key: PeopleSortKey): void => {
  const v = ui.value.peopleView;
  const sortDir = v.sortKey === key && v.sortDir === 'asc' ? 'desc' : 'asc';
  patchUi({ peopleView: { ...v, sortKey: key, sortDir } });
};

export const setPeopleGrouping = (groupBy: PeopleGrouping): void => {
  patchUi({ peopleView: { ...ui.value.peopleView, groupBy, expanded: [] } });
};

export const toggleGroup = (key: string): void => {
  const v = ui.value.peopleView;
  const expanded = v.expanded.includes(key)
    ? v.expanded.filter((k) => k !== key)
    : [...v.expanded, key];
  patchUi({ peopleView: { ...v, expanded } });
};

/**
 * Enter or the End season button: end the season, or open the confirm summary first when the
 * setting asks for it (Section 22 turn flow).
 */
export const requestEndSeason = (): void => {
  if (status.value !== 'deciding') return;
  if (settings.value.confirmEndSeason && !ui.value.confirmingEnd) {
    patchUi({ confirmingEnd: true });
    return;
  }
  endSeason();
};

export const cancelEndSeason = (): void => {
  patchUi({ confirmingEnd: false });
};

export const dismissNotice = (): void => {
  patchUi({ notice: null });
};
